/**
 * Spanish tax ID (NIF/NIE/CIF) format + checksum validation.
 *
 * Built for FASE 2 "Employer KYC — verify company NIF against AEAT before
 * activating an employer account" in docs/13-compliance/AGENCIA-COLOCACION-PLAN.md.
 * This module only validates the CHECKSUM/FORMAT — it does not call AEAT or
 * confirm the number is actually registered/active. That's a separate,
 * later step (needs an AEAT API integration or their NIF-verification
 * webform) once the Employer Portal (B-new-27) has a real employer-account
 * flow to wire it into. Format validation is still useful standalone: it
 * catches typos/fraud at data-entry time before any external call.
 *
 * Three ID shapes, same underlying mod-23 letter checksum:
 *
 *   - NIF (persona física / individual, DNI-based): 8 digits + 1 check
 *     letter. Letter = TRWAGMYFPDXBNJZSQVHLCKE[number % 23].
 *   - NIE (foreign nationals): X/Y/Z + 7 digits + 1 check letter. Leading
 *     X/Y/Z maps to 0/1/2 before applying the same mod-23 table.
 *   - CIF (persona jurídica / company): 1 organization-type letter + 7
 *     digits + 1 control character (digit or letter, depending on the
 *     organization-type letter). Xeito's Employer Portal KYC mainly cares
 *     about this shape, but sole proprietors (autónomos) register as
 *     employers under their personal NIF, so all three are supported.
 *
 * Reference: the algorithm is the standard published Spanish tax-ID
 * checksum (AEAT / Módulo 13 spec), not Xeito-specific.
 */

const NIF_NIE_LETTER_TABLE = 'TRWAGMYFPDXBNJZSQVHLCKE';

// Organization-type letters where the CIF control character must render as
// a DIGIT.
const CIF_DIGIT_ONLY_ORG_LETTERS = new Set(['A', 'B', 'E', 'H']);
// Organization-type letters where the CIF control character must render as
// a LETTER.
const CIF_LETTER_ONLY_ORG_LETTERS = new Set(['K', 'P', 'Q', 'S']);
const CIF_VALID_ORG_LETTERS = new Set([
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'N', 'P', 'Q', 'R', 'S', 'U', 'V', 'W',
]);
const CIF_CONTROL_LETTER_TABLE = 'JABCDEFGHI';

export type SpanishTaxIdType = 'NIF' | 'NIE' | 'CIF';

export interface SpanishTaxIdValidation {
  valid: boolean;
  type: SpanishTaxIdType | null;
  /** Input with whitespace/dashes stripped and letters uppercased. */
  normalized: string;
}

function normalize(raw: string): string {
  return raw.trim().toUpperCase().replace(/[\s-]/g, '');
}

/** Validates a NIF (8 digits + check letter) or NIE (X/Y/Z + 7 digits + check letter). */
export function validateNifOrNie(raw: string): boolean {
  const value = normalize(raw);
  const match = /^([XYZ]?)(\d{7,8})([A-Z])$/.exec(value);
  if (!match) return false;

  const [, prefix, digitsRaw, letter] = match;
  let digits = digitsRaw;

  if (prefix) {
    // NIE: X/Y/Z → 0/1/2, and the digit block is 7 chars (total 8 with prefix).
    if (digitsRaw.length !== 7) return false;
    const prefixDigit = { X: '0', Y: '1', Z: '2' }[prefix];
    digits = prefixDigit + digitsRaw;
  } else if (digitsRaw.length !== 8) {
    return false;
  }

  const expectedLetter = NIF_NIE_LETTER_TABLE[Number(digits) % 23];
  return letter === expectedLetter;
}

/** Validates a CIF (1 org-type letter + 7 digits + 1 control digit-or-letter). */
export function validateCif(raw: string): boolean {
  const value = normalize(raw);
  const match = /^([A-Z])(\d{7})([0-9A-Z])$/.exec(value);
  if (!match) return false;

  const [, orgLetter, digits, control] = match;
  if (!CIF_VALID_ORG_LETTERS.has(orgLetter)) return false;

  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    const digit = Number(digits[i]);
    // 1-indexed odd positions (1st, 3rd, 5th, 7th) are doubled + digit-summed;
    // even positions are summed directly.
    if (i % 2 === 0) {
      const doubled = digit * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    } else {
      sum += digit;
    }
  }

  const controlDigit = (10 - (sum % 10)) % 10;
  const controlLetter = CIF_CONTROL_LETTER_TABLE[controlDigit];

  if (CIF_DIGIT_ONLY_ORG_LETTERS.has(orgLetter)) {
    return control === String(controlDigit);
  }
  if (CIF_LETTER_ONLY_ORG_LETTERS.has(orgLetter)) {
    return control === controlLetter;
  }
  // Either form is accepted for the remaining organization-type letters.
  return control === String(controlDigit) || control === controlLetter;
}

/**
 * Validates any of the three Spanish tax-ID shapes and reports which one
 * matched. Use this for a generic "is this a valid Spanish tax ID" check
 * (e.g. an employer-signup NIF/CIF field) where the caller doesn't know in
 * advance whether the employer is a company (CIF) or a sole proprietor (NIF).
 */
export function validateSpanishTaxId(raw: string): SpanishTaxIdValidation {
  const normalized = normalize(raw);

  if (/^[XYZ]/.test(normalized)) {
    return { valid: validateNifOrNie(normalized), type: 'NIE', normalized };
  }
  if (/^\d/.test(normalized)) {
    return { valid: validateNifOrNie(normalized), type: 'NIF', normalized };
  }
  if (/^[A-Z]/.test(normalized)) {
    return { valid: validateCif(normalized), type: 'CIF', normalized };
  }
  return { valid: false, type: null, normalized };
}
